import React, { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import isBetween from "dayjs/plugin/isBetween"
import { useTranslation } from "react-i18next"
import { DayPicker } from "react-day-picker"
import { Alert, Button, Typography, Tooltip, Flex } from "antd"
import "react-day-picker/style.css"
import { countDays } from "utils/multiDayPricing"
import { useDailyDiscountCalendar } from "hooks/useDailyDiscountCalendar"
import { numberToCurrency } from "helpers/string"
import { useDevice } from "hooks/useDevice"
import { CALENDAR_VARIANTS } from "./constants"
import { findNearestStart, findSpanConflict, isStartOnlyReason } from "./startConflicts"
import styles from "./BookingCalendar.module.css"

dayjs.extend(isBetween)

const { Text } = Typography

const ISO = "YYYY-MM-DD"
const HINT_DATE = "MMM D"

// "-20%": a discounted day's saving on its first guest type (every type moves the same way on a
// day), never shown as 0%.
const formatDiscountPercent = (info) => {
  const row = info?.rows?.[0]
  if (!row?.original) return ""
  return `-${Math.max(1, Math.round(((row.original - row.adjusted) / row.original) * 100))}%`
}

// The longest stay from `from`: the backend's per-start figure (consecutive free days), capped at
// the trip's maximum; the trip's maximum alone when there is no start or no figure for it.
const longestStayFrom = (from, maxSpanByDate, maxDays) => {
  const allowed = from ? Number(maxSpanByDate?.[dayjs(from).format(ISO)]) : 0
  return allowed > 0 ? Math.min(allowed, maxDays || allowed) : maxDays
}

// DayPicker checks `max` against the start the range had BEFORE the click, so clicking a day
// earlier than the start moves the start there and keeps the end — even when the stay from the new
// start can't reach it, e.g. a blackout in between (QA Ticketing #46). The span is checked again
// from the range's real start, and a stay that doesn't fit starts over on the clicked day.
const fitRangeToStart = (range, clickedDay, maxSpanByDate, maxDays) => {
  if (!range?.from || !range?.to) return range
  const longest = longestStayFrom(range.from, maxSpanByDate, maxDays)
  return longest && countDays(range.from, range.to) > longest ? { from: clickedDay, to: undefined } : range
}

/**
 * Inline booking calendar, shared by the guest/embedded flow and the guide's manual booking form.
 *
 * Three shapes, driven by the experience:
 *  - single day        → one date ("Select Start Date")
 *  - fixed multi-day   → one date, but the chosen duration paints the whole block it covers
 *                        (`spanDays`); the picker chooses a start, not an end
 *  - dynamic multi-day → a start/end range ("Select Start and End Date"), constrained
 *                        by the experience's min/max duration
 */
const DateSelector = ({
  value,
  onChange,
  collapsed = false,
  error,
  errorMessage,
  trip,
  isRange,
  minDays,
  maxDays,
  maxSpanByDate,
  availableDates,
  bookedDates,
  blockedReasons,
  unexplainedReasons = [],
  rangeValue,
  label,
  spanDays = 0,
  allowPastDates = false,
  variant = CALENDAR_VARIANTS.INLINE,
  onMonthChange
}) => {
  const [t] = useTranslation()
  const { isMobile } = useDevice()
  const isPopup = variant === CALENDAR_VARIANTS.POPUP
  const [blockedHint, setBlockedHint] = useState(null)
  // A start the guide just clicked that a stay can't begin on, explained under the calendar with the
  // nearest start that works: { iso, text, suggestion }.
  const [startHint, setStartHint] = useState(null)
  // The day under the pointer, whose would-be stay is previewed on the calendar.
  const [hoverIso, setHoverIso] = useState(null)
  // rangeStart/rangeEnd wire the picked stay into the discount calculation so progressive
  // duration pricing (a day-of-stay ramp, not a calendar-date property) can be dynamically
  // evaluated once the range is COMPLETE — day/seasonal keep evaluating on each date's own
  // calendar day regardless. Only passed once both ends are picked (rangeEnd undefined until
  // then), so a lone start date doesn't show progressive-adjusted prices with nothing to
  // index them against yet. Deliberately not passed by the experience-preview calendar, which
  // has no picked stay to index from. See useDailyDiscountCalendar's options doc.
  const { hasPricing, priceInfoForDate, isFlatRate } = useDailyDiscountCalendar(trip, undefined, {
    rangeStart: isRange ? rangeValue?.from : undefined,
    rangeEnd: isRange ? rangeValue?.to : undefined
  })

  /**
   * How long a stay may actually run from the chosen start. The backend reports the real figure per
   * start date (consecutive free days, capped at the trip's maximum); without it a guest can pick a
   * span the validator then rejects on a date the calendar showed as available.
   */
  const effectiveMaxDays = useMemo(
    () => longestStayFrom(rangeValue?.from, maxSpanByDate, maxDays),
    [maxSpanByDate, maxDays, rangeValue?.from]
  )

  /**
   * Start dates the backend says are actually bookable, and dates already taken. When the server
   * supplies these they win: it accounts for whether a whole stay fits from a given day, which the
   * trip-level weekday/season fields below cannot express. An empty or missing list falls back to
   * the local rules rather than disabling the whole calendar.
   */
  const allowedStarts = useMemo(() => (availableDates?.length ? new Set(availableDates) : null), [availableDates])
  const takenDates = useMemo(() => (bookedDates?.length ? new Set(bookedDates) : null), [bookedDates])

  const isDayUnavailable = useCallback(
    (current) => {
      const today = dayjs().startOf("day")
      const date = dayjs(current)
      const scheduledDeletionDate = trip?.guide?.scheduled_deletion_date

      if (!current || !trip) return true

      const iso = date.format(ISO)
      // A free day a stay can't START on (its stay would run into a blackout) is not greyed out: it
      // can sit inside a stay that starts earlier, and as a start it is refused on click, with the
      // reason (QA Ticketing #47). Only a day nothing can use is disabled.
      const isStartOnly = isStartOnlyReason(blockedReasons?.[iso])
      if (takenDates?.has(iso) && !isStartOnly) return true

      // Once a range has a start — whether or not an end is picked yet — every day on or after
      // it is a candidate END of that stay, not a fresh START, INCLUDING re-picking/extending an
      // already-complete range (DayPicker's addToRange treats a later click as moving `to`, not
      // starting over). The backend's start-day list (and the local weekday/season fallback
      // below) only say where a NEW stay may legally begin, and the stay runs continuously from
      // the chosen start regardless. Only real unavailability (already booked, checked above)
      // should stop it, EXCEPT past the far end — see the effectiveMaxDays check below. A day BEFORE
      // the current start still goes through the normal check below, since clicking one moves the
      // start itself, which does need to be a legal start day. Still enforced against `today`: if
      // the range's own start is stale (e.g. editing a booking whose original start date has
      // since passed), a day on/after it can still be a genuinely past date and must stay disabled
      // — unless this caller has already opted into past dates (allowPastDates), e.g. a guide
      // backdating a manual booking.
      if (isRange && rangeValue?.from && !date.isBefore(dayjs(rangeValue.from), "day")) {
        if (!allowPastDates && date.isBefore(today)) return true

        // A day past the longest stay from this start can't END it: DayPicker's `max` range prop
        // (set from effectiveMaxDays below) makes a click there START a new stay on that day
        // instead. So such a day is checked as a start, below, rather than disabled — disabling it
        // left every later date unpickable until the start was cleared (QA Ticketing #40). Mirrors
        // the same off-by-one `max` already uses: effectiveMaxDays is an inclusive day count, so
        // the last valid end date is start + (effectiveMaxDays - 1).
        const daysFromStart = date.diff(dayjs(rangeValue.from).startOf("day"), "day")
        if (!effectiveMaxDays || daysFromStart <= effectiveMaxDays - 1) return false
      }

      if (isStartOnly) return !allowPastDates && date.isBefore(today)
      if (allowedStarts) return (!allowPastDates && date.isBefore(today)) || !allowedStarts.has(iso)

      const weekdayMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
      }

      const blockedWeekdays = trip?.unavailable_weekdays?.map((day) => weekdayMap[day.toLowerCase()]) || []

      const isSoldOut = trip?.sold_out_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
      const isUnavailable = trip?.unavailable_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))

      const hasDateRanges = Array.isArray(trip?.available_date_ranges) && trip.available_date_ranges.length > 0

      let isInAvailableRange = true
      if (hasDateRanges) {
        isInAvailableRange = trip.available_date_ranges.some((range) => {
          const start = dayjs(range.start).startOf("day")
          const end = dayjs(range.end).endOf("day")
          return date.isBetween(start, end, null, "[]")
        })
      }

      return (
        (!allowPastDates && date.isBefore(today)) ||
        date.isAfter(scheduledDeletionDate) ||
        blockedWeekdays.includes(date.day()) ||
        isUnavailable ||
        isSoldOut ||
        (hasDateRanges && !isInAvailableRange)
      )
    },
    [trip, allowedStarts, takenDates, isRange, rangeValue?.from, allowPastDates, effectiveMaxDays, blockedReasons]
  )

  /**
   * A day that can only be refused as a START: free, but a stay beginning there runs into a
   * blackout. Not one while it is a candidate END of the range being built (it then just sits inside
   * that stay), nor a past day this calendar disables anyway.
   */
  const isNoStartDay = useCallback(
    (current) => {
      const date = dayjs(current)
      if (!isStartOnlyReason(blockedReasons?.[date.format(ISO)])) return false
      if (!allowPastDates && date.isBefore(dayjs().startOf("day"))) return false
      if (isRange && rangeValue?.from && !date.isBefore(dayjs(rangeValue.from), "day")) {
        const daysFromStart = date.diff(dayjs(rangeValue.from).startOf("day"), "day")
        return !!effectiveMaxDays && daysFromStart > effectiveMaxDays - 1
      }
      return true
    },
    [blockedReasons, allowPastDates, isRange, rangeValue?.from, effectiveMaxDays]
  )

  // The stay a start would make: the chosen duration (fixed) or the shortest allowed (dynamic).
  const stayLength = isRange ? minDays || 0 : spanDays

  // "A 3-day trip from Nov 5 runs into the Nov 7 blackout." — or the server's own reason when the
  // day it runs into isn't in the loaded window.
  const startConflictText = useCallback(
    (iso) => {
      const conflict = stayLength >= 2 ? findSpanConflict(iso, stayLength, blockedReasons, takenDates) : null
      if (!conflict) return t(`booking.multi_day.blocked_reasons.${blockedReasons?.[iso]}`, { defaultValue: null })
      return t(conflict.reason === "blackout" ? "booking.multi_day.no_start_blackout" : "booking.multi_day.no_start_unavailable", {
        count: stayLength,
        start: dayjs(iso).format(HINT_DATE),
        date: dayjs(conflict.iso).format(HINT_DATE)
      })
    },
    [stayLength, blockedReasons, takenDates, t]
  )

  /**
   * The stay the hovered day would start, painted lighter than a chosen one, with the day it runs
   * into marked: on a fixed trip for any start, on a dynamic one for a start that can't be used.
   */
  const previewModifiers = useMemo(() => {
    if (!hoverIso || stayLength < 2) return {}
    const days = Array.from({ length: stayLength }, (_, offset) => dayjs(hoverIso).add(offset, "day"))
    const conflict = findSpanConflict(hoverIso, stayLength, blockedReasons, takenDates)
    return {
      previewStart: days[0].toDate(),
      previewMiddle: days.slice(1, -1).map((day) => day.toDate()),
      previewEnd: days[days.length - 1].toDate(),
      previewConflict: conflict ? dayjs(conflict.iso).toDate() : []
    }
  }, [hoverIso, stayLength, blockedReasons, takenDates])

  // A click on a day a stay can't start on doesn't select it: the reason, and the nearest start that
  // works, show under the calendar instead of the stay failing on Submit (QA Ticketing #47).
  const refuseStart = (clickedDay) => {
    if (!clickedDay || !isNoStartDay(clickedDay)) return false
    const iso = dayjs(clickedDay).format(ISO)
    setStartHint({ iso, text: startConflictText(iso), suggestion: findNearestStart(clickedDay, allowedStarts, allowPastDates) })
    return true
  }

  const applySuggestedStart = () => {
    const start = startHint?.suggestion
    setStartHint(null)
    if (!start) return
    onChange(isRange ? { from: start.toDate(), to: undefined } : start)
  }

  /**
   * The block a fixed duration covers, painted as a range even though only one day is clicked:
   * first and last day filled, the days between banded.
   */
  const startIso = value ? dayjs(value).format(ISO) : ""
  const spanModifiers = useMemo(() => {
    if (spanDays < 2 || !startIso) return undefined
    const start = dayjs(startIso).startOf("day")
    const end = start.add(spanDays - 1, "day")
    return {
      spanStart: start.toDate(),
      spanEnd: end.toDate(),
      spanMiddle: { after: start.toDate(), before: end.toDate() }
    }
  }, [spanDays, startIso])

  /**
   * While a range is half-selected, hovering a day that would make the range too short or too long
   * explains why it will not take, instead of the click silently doing nothing.
   */
  const handleDayMouseEnter = useCallback(
    (date, modifiers) => {
      const isPreviewed = modifiers?.noStart || (!isRange && !modifiers?.disabled)
      setHoverIso(isPreviewed ? dayjs(date).format(ISO) : null)
      if (!isRange) return
      const start = rangeValue?.from
      if (!start || rangeValue?.to) {
        setBlockedHint(null)
        return
      }
      if (dayjs(date).isBefore(dayjs(start), "day")) {
        setBlockedHint(null)
        return
      }
      const days = countDays(start, date)
      if (minDays && days < minDays) setBlockedHint({ iso: dayjs(date).format(ISO), text: t("booking.multi_day.min_days", { count: minDays }) })
      else if (effectiveMaxDays && days > effectiveMaxDays)
        setBlockedHint({ iso: dayjs(date).format(ISO), text: t("booking.multi_day.max_days", { count: effectiveMaxDays }) })
      else setBlockedHint(null)
    },
    [isRange, rangeValue?.from, rangeValue?.to, minDays, effectiveMaxDays, t]
  )

  // Why a disabled day is disabled — an existing booking, a blackout, or the season boundary —
  // so a guest isn't left guessing why one plain-looking date is unclickable and its neighbor
  // isn't. Backend-driven (PrivateSlotsFinder#multi_day_block_reasons): a date can be blocked
  // for a reason that has nothing to do with the date itself (e.g. its stay would run into an
  // already-booked day further along), which no amount of local inference could reconstruct.
  const blockReasonText = useCallback(
    (iso) => {
      const reason = blockedReasons?.[iso]
      if (!reason || unexplainedReasons.includes(reason)) return null

      return t(`booking.multi_day.blocked_reasons.${reason}`, { defaultValue: null })
    },
    [blockedReasons, unexplainedReasons, t]
  )

  const DayButton = useCallback(
    ({ day, modifiers, children, ...buttonProps }) => {
      const iso = dayjs(day.date).format(ISO)
      const isBlocked = blockedHint?.iso === iso
      const disabledReason = modifiers?.disabled ? blockReasonText(iso) : null
      const info = hasPricing ? priceInfoForDate(dayjs(day.date)) : null
      // The literal day(s) the guest clicked — a single date, a range's two ENDPOINTS, or
      // (fixed-duration single-click block) the span's first/last day. Deliberately excludes a
      // range's middle days: react-day-picker sets `modifiers.selected` for every day in a
      // completed range, not just its endpoints, so checking `selected` alone painted the exact
      // same solid circle on the start, middle, and end days alike — indistinguishable from each
      // other. `range_middle` is react-day-picker's own, more specific modifier for exactly those
      // days, so excluding it here is what lets a middle day fall through to the plain (not
      // "selected") day-number style instead, while its own lighter background band (the
      // library's default `.rdp-range_middle` cell styling, untouched by the neutralization CSS
      // below) still shows it's part of the range. react-day-picker paints its own
      // selected/range-endpoint fill on the whole button by default, which is why a second
      // (price) line used to clip against the circle's curved edge — that fill is neutralized in
      // CSS, and painted here instead on just the day-number span, so the price pill below it has
      // its own room.
      const isSelected = Boolean(
        modifiers?.range_start ||
          modifiers?.range_end ||
          modifiers?.spanStart ||
          modifiers?.spanEnd ||
          (modifiers?.selected && !modifiers?.range_middle)
      )

      const button = (
        <button {...buttonProps} className={`${buttonProps.className || ""} ${styles.calendarDayButton}`}>
          <span className={isSelected ? styles.calendarDayNumberSelected : styles.calendarDayNumber}>{children}</span>
          {info?.hasDiscount && (
            <span className={isSelected ? styles.calendarDayPriceSelected : styles.calendarDayPrice}>
              {isPopup ? formatDiscountPercent(info) : numberToCurrency(info.headline)}
            </span>
          )}
          {info?.hasDiscount && <span className={styles.calendarDayDot} />}
        </button>
      )

      // The "range too short/long" hint (while actively building a range) takes priority
      // over the discount breakdown — it's live feedback about the click that just happened.
      if (isBlocked) {
        return (
          <Tooltip open title={blockedHint.text} placement="top">
            {button}
          </Tooltip>
        )
      }

      // Explains a disabled day on hover — takes priority over the discount breakdown since a
      // disabled day can't be booked at that price anyway. The button itself is a disabled
      // native <button> (react-day-picker sets the HTML `disabled` attribute), which never
      // fires hover events in any browser — Tooltip is attached to a non-disabled wrapper span
      // instead, or it would silently never open.
      if (disabledReason) {
        return (
          <Tooltip title={disabledReason} placement="top">
            <span className={styles.disabledDayWrapper}>{button}</span>
          </Tooltip>
        )
      }

      // A free day a stay can't start on: says why on hover, before any click.
      if (modifiers?.noStart) {
        return (
          <Tooltip title={startConflictText(iso)} placement="top">
            {button}
          </Tooltip>
        )
      }

      // No prices on a day that can't be booked, explained or not (e.g. a guide-side blackout day).
      if (!info?.hasDiscount || modifiers?.disabled) return button

      return (
        <Tooltip
          placement="top"
          title={
            <Flex vertical gap={4} className={styles.discountTooltipContent}>
              <Text strong className={styles.discountTooltipHeading}>
                {isFlatRate
                  ? t("listings.tabs.preview.pricing_preview.min_price", { defaultValue: "Min. price:" })
                  : t("listings.tabs.preview.pricing_preview.min_price_per_person", {
                      defaultValue: "Min. price per person:"
                    })}
              </Text>
              {info.rows.map((row) => (
                <Flex key={row.key} justify="space-between" gap={12}>
                  <span className={styles.discountTooltipLabel}>{row.label}</span>
                  <span>
                    <Text delete className={styles.discountTooltipOriginal}>
                      {numberToCurrency(row.original)}
                    </Text>
                    <Text strong className={styles.discountTooltipAdjusted}>
                      {numberToCurrency(row.adjusted)}
                    </Text>
                  </span>
                </Flex>
              ))}
            </Flex>
          }
        >
          {button}
        </Tooltip>
      )
    },
    [blockedHint, hasPricing, priceInfoForDate, isFlatRate, t, blockReasonText, isPopup, startConflictText]
  )

  if (collapsed) {
    let summary = value && dayjs(value).format("MM/DD/YY")
    if (isRange) {
      summary = [rangeValue?.from, rangeValue?.to].filter(Boolean).map((date) => dayjs(date).format("MM/DD/YY")).join(" – ")
    } else if (value && spanDays >= 2) {
      summary = `${summary} – ${dayjs(value).add(spanDays - 1, "day").format("MM/DD/YY")}`
    }
    return (
      <div className={styles.inputContainer}>
        <span className={styles.inputLabel}>{t("booking.multi_day.date_label")}</span>
        <span className={styles.summaryValue}>{summary || "—"}</span>
      </div>
    )
  }

  const commonProps = {
    captionLayout: "label",
    // Availability gates the day that is picked. A multi-day block runs continuously from there,
    // so the days it covers are not re-checked — an experience that only runs Wed–Fri would
    // otherwise have no legal start at all for anything longer than three days.
    disabled: isDayUnavailable,
    // Two months side by side would repeat each other's edge days.
    showOutsideDays: !isPopup,
    onDayMouseEnter: handleDayMouseEnter,
    onDayMouseLeave: () => {
      setBlockedHint(null)
      setHoverIso(null)
    },
    // The first month on show, for a caller that loads availability a month at a time (ticketed).
    onMonthChange,
    components: { DayButton },
    modifiersClassNames: {
      noStart: styles.noStartDay,
      previewStart: styles.previewStart,
      previewMiddle: styles.previewMiddle,
      previewEnd: styles.previewEnd,
      previewConflict: styles.previewConflict,
      spanStart: styles.spanStart,
      spanMiddle: styles.spanMiddle,
      spanEnd: styles.spanEnd
    },
    // The popup shows two months side by side with one-letter weekdays ("S M T W T F S"); a phone
    // has room for one.
    ...(isPopup && {
      numberOfMonths: isMobile ? 1 : 2,
      formatters: { formatWeekdayName: (weekday) => dayjs(weekday).format("dd").charAt(0) }
    })
  }

  return (
    <div className={styles.wrapper}>
      <div className={[styles.inlineCalendar, isPopup && styles.popupCalendar, error && styles.errorBorder].filter(Boolean).join(" ")}>
        {label && <div className={styles.calendarLabel}>{label}</div>}
        {hasPricing && (
          <div className={styles.legend}>
            <span className={styles.legendDot} />
            <Text type="secondary">
              {t("listings.tabs.preview.pricing_preview.discounted_days", { defaultValue: "Discounted days" })}
            </Text>
          </div>
        )}
        {isRange ? (
          <DayPicker
            {...commonProps}
            mode="range"
            // DayPicker's own min/max count the GAP between from/to (differenceInCalendarDays),
            // not the inclusive day count minDays/maxDays mean everywhere else in this app (and
            // everywhere else in this file, e.g. the hover-hint math below) — so a trip whose
            // maxDays is 8 needs max=7 here, or DayPicker allows a 9-day span (8 nights + both
            // endpoints). Same off-by-one applies to min.
            min={minDays ? minDays - 1 : undefined}
            max={effectiveMaxDays ? effectiveMaxDays - 1 : undefined}
            modifiers={{ noStart: isNoStartDay, ...previewModifiers }}
            selected={rangeValue}
            onSelect={(range, clickedDay) => {
              if (refuseStart(clickedDay)) return
              setStartHint(null)
              onChange(fitRangeToStart(range, clickedDay, maxSpanByDate, maxDays))
            }}
            defaultMonth={rangeValue?.from ?? dayjs().startOf("month").toDate()}
          />
        ) : (
          <DayPicker
            {...commonProps}
            mode="single"
            modifiers={{ noStart: isNoStartDay, ...spanModifiers, ...previewModifiers }}
            selected={value ? dayjs(value).toDate() : undefined}
            // react-day-picker's single mode calls onSelect(undefined) when the guide clicks
            // the already-selected day again (deselecting it) — forwarded through as `null` so
            // the guide can clear a pick and choose a different date, instead of being stuck
            // once any date is selected.
            onSelect={(date, clickedDay) => {
              if (refuseStart(clickedDay)) return
              setStartHint(null)
              onChange(date ? dayjs(date) : null)
            }}
            defaultMonth={value ? dayjs(value).toDate() : dayjs().startOf("month").toDate()}
          />
        )}
      </div>
      {startHint && (
        <Alert
          type="warning"
          showIcon
          className={styles.startHint}
          message={startHint.text}
          action={
            startHint.suggestion && (
              <Button type="link" size="small" onClick={applySuggestedStart}>
                {t("booking.multi_day.start_instead", { date: startHint.suggestion.format(HINT_DATE) })}
              </Button>
            )
          }
        />
      )}
      {error && <Text className={styles.errorText}>{errorMessage || t("webIntegration.embedded_validation.date")}</Text>}
    </div>
  )
}

DateSelector.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  trip: PropTypes.object,
  isRange: PropTypes.bool,
  minDays: PropTypes.number,
  maxDays: PropTypes.number,
  maxSpanByDate: PropTypes.objectOf(PropTypes.number),
  availableDates: PropTypes.arrayOf(PropTypes.string),
  bookedDates: PropTypes.arrayOf(PropTypes.string),
  blockedReasons: PropTypes.objectOf(PropTypes.string),
  // Block reasons whose disabled day gets no hover explanation (the guide's calendar: a blackout day).
  unexplainedReasons: PropTypes.arrayOf(PropTypes.string),
  rangeValue: PropTypes.shape({ from: PropTypes.instanceOf(Date), to: PropTypes.instanceOf(Date) }),
  label: PropTypes.string,
  spanDays: PropTypes.number,
  allowPastDates: PropTypes.bool,
  variant: PropTypes.oneOf(Object.values(CALENDAR_VARIANTS)),
  onMonthChange: PropTypes.func
}

export default React.memo(DateSelector)
